import { isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { removeEmojiPrefixFromLabel } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { FormBuilderTextInput } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/FormBuilderTextInput';
import { SimpleSwitch } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/SimpleSwitch';
import { ILocalTargetingUI } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  fontSizing,
  Row,
  Rows,
  spacing,
  Text,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  generateCitiesOptionsViaCountry,
  generateCitiesOptionsViaState,
  generateStateOptions,
} from '~/src/dash/utils/countries';

const rowHeight = 32;
const labelWidth = 64;

const commaLabelStyle = {
  fontSize: fontSizing.extraSmall,
  lineHeight: '14px',
};

const filterViableOptionIDs = (
  selectedIDs: Set<string>,
  options: ISelectOption[] = [],
): Set<string> => {
  const viableSet: Set<string> = new Set([]);
  const optionKeys = new Set(options.map((option) => option.id));
  selectedIDs.forEach((id) => {
    if (optionKeys.has(id)) {
      viableSet.add(id);
    }
  });
  return viableSet;
};

const generateCityOptions = (
  country: string,
  numStateOptions: number,
  areStatesCommaSeparated: boolean,
  selectedStateIDs: Set<string>,
  stateText: string,
) => {
  if (!country) return [];
  if (!numStateOptions) {
    return generateCitiesOptionsViaCountry(country.toUpperCase());
  }

  const statesArray = stateText ? stateText.split(',') : [];
  if (areStatesCommaSeparated && statesArray.length === 1) {
    return generateCitiesOptionsViaState(country.toUpperCase(), stateText.toUpperCase());
  } else if (!areStatesCommaSeparated && selectedStateIDs.size === 1) {
    return generateCitiesOptionsViaState(
      country.toUpperCase(),
      Array.from(selectedStateIDs)[0].toUpperCase(),
    );
  }

  return [];
};

const convertCommaStringIntoSet = (string: string) => {
  if (!string) return new Set([]);
  return new Set(string.split(',').map((s) => s.trim()));
};

const convertCommaStringIntoUpperCaseSet = (string: string) => {
  if (!string) return new Set([]);
  return new Set(string.split(',').map((s) => s.trim().toUpperCase()));
};

interface IProps {
  location: ILocalTargetingUI;
  index: number;
  isRemoveDisabled: boolean;
  key: number | string;
  handleRemoveLocation: (idx: number) => void;
}

export const LocationForm = ({
  location,
  index,
  isRemoveDisabled,
  handleRemoveLocation,
}: IProps) => {
  const { lists } = useFormBuilderStore();

  const isMounted = useRef(false);
  // COUNTRY
  const [selectedCountryID, setSelectedCountryID] = useState(location.country || '');
  // STATES
  const [stateOptions, setStateOptions] = useState<ISelectOption[]>(
    generateStateOptions(location.country || ''),
  );
  const [selectedStateIDs, setSelectedStateIDs] = useState<Set<string>>(
    new Set(location.states ? location.states.split(',') : []),
  );
  const [stateText, setStateText] = useState(location?.states || '');
  const [stateCounter, setStateCounter] = useState(1);
  const [areStatesCommaSeparated, setAreStatesCommaSeparated] = useState(
    !!location.areStatesCommaSeparated,
  );
  // CITIES
  const [cityOptions, setCityOptions] = useState<ISelectOption[]>(
    generateCityOptions(
      selectedCountryID,
      stateOptions.length,
      areStatesCommaSeparated,
      selectedStateIDs,
      stateText,
    ),
  );
  const [areCitiesLoaded, setAreCitiesLoaded] = useState(true);
  const [selectedCityIDs, setSelectedCityIDs] = useState<Set<string>>(
    new Set(location.cities ? location.cities.split(',') : []),
  );
  const [cityText, setCityText] = useState(location?.cities || '');
  const [areCitiesCommaSeparated, setAreCitiesCommaSeparated] = useState(
    !!location.areCitiesCommaSeparated,
  );
  // ZIPS
  const [zipText, setZipText] = useState(location?.zips || '');
  // DMAS
  const [selectedDMAs, setSelectedDMAs] = useState(
    new Set(location.dmas ? location.dmas.split(',') : []),
  );
  const [dmaText, setDMAText] = useState(location?.dmas || '');
  const [areDMAsCommaSeparated, setAreDMAsCommaSeparated] = useState(
    !!location.areDMAsCommaSeparated,
  );

  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setCityOptions(
          generateCityOptions(
            selectedCountryID,
            stateOptions.length,
            areStatesCommaSeparated,
            selectedStateIDs,
            stateText,
          ),
        );
        setAreCitiesLoaded(true);
      });
    } else {
      isMounted.current = true;
    }
  }, [selectedCountryID, stateOptions, areStatesCommaSeparated, selectedStateIDs, stateText]);

  const clearStates = () => {
    location.states = '';
    location.cities = '';
    location.zips = '';
    unstable_batchedUpdates(() => {
      setSelectedStateIDs(new Set([]));
      setStateText('');
      setSelectedCityIDs(new Set([]));
      setCityText('');
      setStateCounter(stateCounter + 1);
      setZipText('');
    });
  };

  const clearCities = () => {
    location.cities = '';
    unstable_batchedUpdates(() => {
      setSelectedCityIDs(new Set([]));
      setCityText('');
      setStateCounter(stateCounter + 1);
    });
  };

  const handleApplyCountry = (value: Set<string>) => {
    const activeCountryID = Array.from(value)[0];
    location.country = activeCountryID;
    location.states = '';
    location.zips = '';
    location.cities = '';
    location.dmas = '';
    generateStateOptions(activeCountryID);
    unstable_batchedUpdates(() => {
      setSelectedCountryID(activeCountryID);
      setSelectedStateIDs(new Set([]));
      setStateText('');
      setStateOptions(generateStateOptions(activeCountryID));
      setSelectedCityIDs(new Set([]));
      setCityText('');
      setZipText('');
      setAreCitiesLoaded(false);
      setDMAText('');
      setSelectedDMAs(new Set([]));
    });
  };

  const handleApplyState = (value: Set<string>) => {
    if (!value.size) {
      clearStates();
    } else {
      const states: string[] = [];
      value.forEach((s) => {
        states.push(s.trim());
      });
      location.states = states.join(',');
      unstable_batchedUpdates(() => {
        setSelectedStateIDs(value);
        clearCities();
        setAreCitiesLoaded(false);
      });
    }
  };

  const handleChangeStateText = (text: string) => {
    if (!text) {
      clearStates();
    } else {
      unstable_batchedUpdates(() => {
        clearCities();
        setStateText(text);
        setStateCounter(stateCounter + 1);
      });
      location.states = text;
    }
  };

  const handleApplyCity = (value: Set<string>) => {
    setSelectedCityIDs(value);
    if (!value.size) {
      location.cities = '';
    } else {
      const cities: string[] = [];
      value.forEach((s) => {
        cities.push(s.trim());
      });
      location.cities = cities.join(',');
    }
  };

  const handleChangeCityText = (text: string) => {
    setCityText(text);
    location.cities = text;
  };

  const handleChangeZipText = (text: string) => {
    setZipText(text);
    location.zips = text;
  };

  const handleApplyDMA = (value: Set<string>) => {
    setSelectedDMAs(value);
    if (!value.size) {
      location.dmas = '';
    } else {
      const dmas: string[] = [];
      value.forEach((s) => {
        dmas.push(s.trim());
      });
      location.dmas = dmas.join(',');
    }
  };

  const handleChangeDMAText = (text: string) => {
    setDMAText(text);
    location.dmas = text;
  };

  const handleStatesCommaToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isCommaEnabled = e.target.checked === true;
    if (isCommaEnabled) {
      unstable_batchedUpdates(() => {
        setStateText(Array.from(selectedStateIDs).join(','));
        setAreStatesCommaSeparated(e.target.checked);
      });
    } else {
      const filteredSelectedStateIDs = filterViableOptionIDs(
        convertCommaStringIntoUpperCaseSet(stateText),
        stateOptions || [],
      );
      unstable_batchedUpdates(() => {
        setSelectedStateIDs(filteredSelectedStateIDs);
        setAreStatesCommaSeparated(e.target.checked);
      });
    }
    location.areStatesCommaSeparated = e.target.checked;
  };

  const handleDMAsCommaToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isCommaEnabled = e.target.checked === true;
    if (isCommaEnabled) {
      unstable_batchedUpdates(() => {
        setDMAText(Array.from(selectedDMAs).join(','));
        setAreDMAsCommaSeparated(e.target.checked);
      });
    } else {
      const filteredSelectedDMAs = filterViableOptionIDs(
        convertCommaStringIntoSet(dmaText),
        lists.DMAs || [],
      );
      unstable_batchedUpdates(() => {
        setSelectedDMAs(filteredSelectedDMAs);
        setAreDMAsCommaSeparated(e.target.checked);
      });
    }
    location.areDMAsCommaSeparated = e.target.checked;
  };

  const handleCitiesCommaToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isCommaEnabled = e.target.checked === true;
    if (isCommaEnabled) {
      unstable_batchedUpdates(() => {
        setCityText(Array.from(selectedCityIDs).join(','));
        setAreCitiesCommaSeparated(e.target.checked);
      });
    } else {
      const filteredSelectedCityIDs = filterViableOptionIDs(
        convertCommaStringIntoSet(cityText),
        cityOptions || [],
      );
      unstable_batchedUpdates(() => {
        setSelectedCityIDs(filteredSelectedCityIDs);
        setAreCitiesCommaSeparated(e.target.checked);
      });
    }
    location.areCitiesCommaSeparated = e.target.checked;
  };

  const isDMAsDisabled = selectedCountryID !== 'us';
  const isCitiesDisabled =
    !selectedCountryID ||
    (!areStatesCommaSeparated && selectedStateIDs.size > 1) ||
    (areStatesCommaSeparated && stateText.split(',').length > 1) ||
    (selectedStateIDs.size === 0 && !stateText && !isEmpty(stateOptions));

  return (
    <Rows
      gap={spacing.small}
      smallVerticalPadding
      horizontalPadding
      style={{
        border: `1px solid ${publicaColors.lightGray}`,
        borderRadius: borderRadius.base,
        backgroundColor: publicaColors.white,
      }}
    >
      <Row noPadding>
        <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Col paddingTop=''>
            <Text extraSmall color={publicaColors.gray}>
              Local Targeting
            </Text>
          </Col>
          <Col>
            <Div
              onClick={() => !isRemoveDisabled && handleRemoveLocation(index)}
              style={{ cursor: isRemoveDisabled ? 'default' : 'pointer' }}
            >
              <IconV2
                name='close'
                color={isRemoveDisabled ? publicaColors.lightGray : publicaColors.veryDarkGray}
              />
            </Div>
          </Col>
        </Cols>
      </Row>
      {/* COUNTRY */}
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: `${labelWidth}px` }}>
            <Text small>Country</Text>
          </Col>
          <Col flex style={{ maxWidth: '400px' }}>
            <IndependentSelect
              enableLabelTooltip
              handleApplySelect={handleApplyCountry}
              listLabel='Countries'
              options={lists.Countries}
              placeholder='Select A Country'
              selectedLimit={1}
              isSelectionRequired
              selectedOptionIDs={new Set(location.country ? [location.country] : [])}
              singularLabel='Country'
              key='Country'
              maxWidth={400}
              extractStringToSort={removeEmojiPrefixFromLabel}
            />
          </Col>
        </Cols>
      </Row>
      {/* STATES */}
      {selectedCountryID && (
        <Row noPadding height={rowHeight}>
          <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: `${labelWidth}px` }}>
              <Text small>States</Text>
            </Col>
            <Col flex style={{ maxWidth: '280px' }}>
              {areStatesCommaSeparated ? (
                <FormBuilderTextInput
                  placeholder='Enter States Comma Separated'
                  initialValue={Array.from(selectedStateIDs).join(',')}
                  handleOnChange={handleChangeStateText}
                  key={selectedCountryID}
                  fullWidth
                  inputID={'States'}
                  msDelay={300}
                />
              ) : (
                <IndependentSelect
                  enableLabelTooltip
                  handleApplySelect={handleApplyState}
                  listLabel='States'
                  options={stateOptions}
                  placeholder='Select States'
                  selectedOptionIDs={selectedStateIDs}
                  singularLabel='State'
                  key={selectedCountryID}
                  isDisabled={!selectedCountryID || isEmpty(stateOptions)}
                  maxWidth={256}
                />
              )}
            </Col>
            <Col>
              <SimpleSwitch
                isChecked={areStatesCommaSeparated}
                handleSwitch={handleStatesCommaToggle}
                key={selectedCountryID}
                inputID={selectedCountryID + '_state'}
                label={'Comma Separated'}
                labelStyle={{
                  color: areStatesCommaSeparated ? 'inherit' : publicaColors.gray,
                  ...commaLabelStyle,
                }}
                small
              />
            </Col>
          </Cols>
        </Row>
      )}
      {/* ZIP CODES */}
      {selectedCountryID && (
        <Row noPadding height={rowHeight} key={stateCounter}>
          <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: `${labelWidth}px` }}>
              <Text small>Zip</Text>
            </Col>
            <Col flex style={{ maxWidth: '400px' }}>
              <FormBuilderTextInput
                placeholder='Enter Zip Codes Comma Separated'
                initialValue={zipText}
                handleOnChange={handleChangeZipText}
                key={selectedCountryID}
                fullWidth
                inputID={selectedCountryID + '_zipcodes'}
                isDisabled={false}
                msDelay={300}
              />
            </Col>
          </Cols>
        </Row>
      )}
      {/* DMAS (US ONLY) */}
      {!isDMAsDisabled && (
        <Row noPadding height={rowHeight}>
          <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: `${labelWidth}px` }}>
              <Text small>DMAs</Text>
            </Col>
            <Col flex style={{ maxWidth: '280px' }}>
              {areDMAsCommaSeparated ? (
                <FormBuilderTextInput
                  placeholder='Enter DMAs Comma Separated'
                  initialValue={Array.from(selectedDMAs).join(',')}
                  handleOnChange={handleChangeDMAText}
                  key={selectedCountryID}
                  fullWidth
                  inputID={'DMAs'}
                  isDisabled={isDMAsDisabled}
                  msDelay={300}
                />
              ) : (
                <IndependentSelect
                  enableLabelTooltip
                  handleApplySelect={handleApplyDMA}
                  listLabel='DMAs'
                  options={lists.DMAs}
                  placeholder='Select DMAs'
                  selectedOptionIDs={selectedDMAs}
                  singularLabel='DMA'
                  key={selectedCountryID}
                  isDisabled={isDMAsDisabled}
                  maxWidth={256}
                />
              )}
            </Col>
            <Col>
              <SimpleSwitch
                isChecked={areDMAsCommaSeparated}
                handleSwitch={handleDMAsCommaToggle}
                key={selectedCountryID}
                inputID={selectedCountryID + '_dmas'}
                label={'Comma Separated'}
                labelStyle={{
                  color: areDMAsCommaSeparated ? 'inherit' : publicaColors.gray,
                  ...commaLabelStyle,
                }}
                small
              />
            </Col>
          </Cols>
        </Row>
      )}
      {/* CITIES */}
      {!isCitiesDisabled && areCitiesLoaded && (
        <Row noPadding height={rowHeight}>
          <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: `${labelWidth}px` }}>
              <Text small>Cities</Text>
            </Col>
            <Col flex style={{ maxWidth: '280px' }}>
              {areCitiesCommaSeparated ? (
                <FormBuilderTextInput
                  placeholder='Enter Cities Comma Separated'
                  initialValue={Array.from(selectedCityIDs).join(',')}
                  handleOnChange={handleChangeCityText}
                  key={selectedCountryID + stateCounter}
                  fullWidth
                  inputID={'Cities'}
                  isDisabled={isCitiesDisabled}
                  msDelay={300}
                />
              ) : (
                <IndependentSelect
                  enableLabelTooltip
                  handleApplySelect={handleApplyCity}
                  listLabel='Cities'
                  options={cityOptions}
                  placeholder='Select Cities'
                  selectedOptionIDs={selectedCityIDs}
                  singularLabel='City'
                  key={Array.from(selectedStateIDs).toString()}
                  isDisabled={isCitiesDisabled}
                  maxWidth={256}
                />
              )}
            </Col>
            <Col>
              <SimpleSwitch
                isChecked={areCitiesCommaSeparated}
                handleSwitch={handleCitiesCommaToggle}
                key={selectedCountryID + stateCounter}
                inputID={selectedCountryID + '_cities'}
                label={'Comma Separated'}
                labelStyle={{
                  color: areCitiesCommaSeparated ? 'inherit' : publicaColors.gray,
                  ...commaLabelStyle,
                }}
                small
              />
            </Col>
          </Cols>
        </Row>
      )}
    </Rows>
  );
};
